import { Link, useLocation, useNavigate } from "react-router-dom";
import { POSEIDON } from "../lib/defaults";
import { useAuth } from "../lib/auth";
import { LogOut, User, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Header() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, team, isStaff, logout } = useAuth();
  const [open, setOpen] = useState(false);

  function doLogout() {
    logout();
    setOpen(false);
    nav("/login");
  }

  const navLinks = [
    { to: "/", label: "Calc", testId: "nav-calculator", show: !!user },
    { to: "/history", label: "History", testId: "nav-history", show: !!user },
    { to: "/team", label: "Team", testId: "nav-team", show: !!user },
    { to: "/billing", label: "Billing", testId: "nav-billing", show: user?.role === "admin" },
    { to: "/admin/promo-codes", label: "Promo", testId: "nav-promo", show: isStaff },
  ];

  return (
    <header className="bg-black border-b-4 border-[#F26522] sticky top-0 z-50" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to={user ? "/" : "/login"} className="flex items-center gap-3 min-w-0" data-testid="logo-link">
          <div className="w-12 h-12 bg-white p-1 shrink-0">
            <img src={POSEIDON.logo} alt="Poseidon" className="w-full h-full object-contain" />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-xl sm:text-2xl font-black tracking-tight leading-none uppercase text-white">
              Poseidon
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#F26522] leading-none mt-1 uppercase truncate">
              ProDose{team?.name ? ` · ${team.name}` : ""}
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        {user && (
          <nav className="hidden md:flex items-center gap-0">
            {navLinks.filter(l => l.show).map(l => {
              const active = loc.pathname === l.to;
              return (
                <Link key={l.to} to={l.to} data-testid={l.testId}
                  className={`px-4 h-12 flex items-center font-heading font-black text-sm tracking-wider uppercase transition-colors ${
                    active ? "bg-[#F26522] text-black" : "text-white hover:bg-white/10"
                  }`}>
                  {l.label}
                </Link>
              );
            })}
            <div className="ml-3 flex items-center gap-2 pl-3 border-l-2 border-white/15">
              <div className="hidden lg:block text-right">
                <div className="font-mono text-[11px] text-white truncate max-w-[180px]">{user.email}</div>
                <div className="font-mono text-[9px] tracking-wider text-[#F26522] uppercase">{user.role}</div>
              </div>
              <button onClick={doLogout} data-testid="btn-logout"
                className="w-10 h-10 border-2 border-white/15 hover:border-[#F26522] hover:text-[#F26522] text-white/70 flex items-center justify-center transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </nav>
        )}

        {!user && (
          <nav className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="px-4 h-12 flex items-center font-heading font-black text-sm tracking-wider uppercase text-white hover:bg-white/10 transition-colors">Log In</Link>
            <Link to="/signup" data-testid="nav-signup" className="px-4 h-12 flex items-center bg-[#F26522] text-black font-heading font-black text-sm tracking-wider uppercase hover:bg-[#FF7A2A] transition-colors">Start Team</Link>
          </nav>
        )}

        {/* Mobile burger */}
        {user && (
          <button onClick={() => setOpen(s => !s)} data-testid="btn-mobile-menu"
            className="md:hidden w-10 h-10 border-2 border-white/15 text-white flex items-center justify-center">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}
      </div>

      {/* Mobile dropdown */}
      {user && open && (
        <div className="md:hidden bg-[#111111] border-t-2 border-[#F26522] px-4 py-3 space-y-1" data-testid="mobile-menu">
          {navLinks.filter(l => l.show).map(l => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className={`block px-4 h-12 leading-[3rem] font-heading font-black text-sm tracking-wider uppercase ${
                loc.pathname === l.to ? "bg-[#F26522] text-black" : "text-white"
              }`}>
              {l.label}
            </Link>
          ))}
          <div className="px-4 py-2 border-t border-white/10 mt-2">
            <div className="font-mono text-[10px] text-white/70 mb-2">{user.email}</div>
            <button onClick={doLogout}
              className="w-full h-12 border-2 border-white/15 text-white font-heading font-black text-sm tracking-wider uppercase flex items-center justify-center gap-2 hover:border-[#F26522] hover:text-[#F26522]">
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
